import { Controller } from "@hotwired/stimulus"

// Optional, transient Mapbox reference. No itinerary writes or provider-derived safety claims.
export default class extends Controller {
  static targets = ["canvas", "status", "load", "preview", "options", "style"]
  static values = { token: String, points: Array, routes: Array }

  connect() { this.generation = 0 }
  disconnect() { this.cancel() }

  cancel() {
    this.generation += 1
    this.abort?.abort()
    clearTimeout(this.deadline)
    this.map?.remove()
    this.map = null
  }

  async load() {
    this.cancel()
    const generation = this.generation
    this.abort = new AbortController()
    this.deadline = setTimeout(() => this.abort.abort(), 12000)
    this.loadTarget.disabled = true
    this.previewTarget.hidden = true
    this.optionsTarget.replaceChildren()
    this.statusTarget.textContent = "Loading map preview… Your itinerary remains available."
    try {
      if (!this.tokenValue || !this.tokenValue.startsWith("pk.")) throw new Error("Map access is not configured.")
      await this.library()
      if (!window.mapboxgl.supported()) throw new Error("This browser cannot display the map.")
      this.canvasTarget.hidden = false
      this.map = new window.mapboxgl.Map({container:this.canvasTarget, accessToken:this.tokenValue, style:this.styleTarget.value, center:[-96,38], zoom:3})
      await this.wait((resolve,reject) => {
        this.map.once("load",resolve)
        this.map.once("error",reject)
      })
      this.map.addControl(new window.mapboxgl.NavigationControl())
      this.points = []
      const matches = []
      for (const point of this.pointsValue) {
        let coordinates = point.coordinates
        if (!this.coordinates(coordinates) && point.endpoint && point.name) {
          const data = await this.json(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(point.name)}.json?limit=1&access_token=${encodeURIComponent(this.tokenValue)}`)
          const match = data.features?.[0]
          if (this.coordinates(match?.center)) {
            coordinates = match.center
            matches.push(match.place_name)
          }
        }
        if (this.coordinates(coordinates)) this.points.push({...point, coordinates})
      }
      if (generation !== this.generation) return
      const bounds = new window.mapboxgl.LngLatBounds()
      for (const point of this.points) {
        new window.mapboxgl.Marker({color:point.endpoint ? "#aeb6b9" : "#eabb67"})
          .setLngLat(point.coordinates).setPopup(new window.mapboxgl.Popup().setText(point.name)).addTo(this.map)
        bounds.extend(point.coordinates)
      }
      if (!bounds.isEmpty()) this.map.fitBounds(bounds,{padding:45,maxZoom:10,duration:0})
      this.drawTruckRoutes()
      const omitted = this.pointsValue.length - this.points.length
      this.statusTarget.textContent = `${matches.length ? "Approximate endpoint matches: " + matches.join(" → ") + ". " : "Saved mapped points. "}${omitted ? omitted + " unmapped stops or endpoints are not included. " : ""}Review the points before requesting general-driving directions. Matches are not saved.`
      this.previewTarget.hidden = !(this.points.length >= 2 && this.pointsValue.filter(p=>p.endpoint).every(p=>this.points.some(q=>q.key===p.key)))
      this.previewTarget.textContent = omitted ? "Preview mapped points only" : "Preview these points"
      this.map.on("error", () => { this.statusTarget.textContent = "Map preview unavailable. Your itinerary and edits are unaffected." })
    } catch (_) {
      if (generation === this.generation) {
        this.statusTarget.textContent = "Map preview unavailable. Your itinerary and edits are unaffected. Try loading it again."
        this.canvasTarget.hidden = true
        this.map?.remove()
        this.map = null
      }
    } finally {
      if (generation === this.generation) {
        clearTimeout(this.deadline)
        this.loadTarget.disabled = false
      }
    }
  }

  async preview() {
    this.previewTarget.disabled = true
    this.optionsTarget.replaceChildren()
    this.statusTarget.textContent = "Loading general-driving directions…"
    this.deadline = setTimeout(()=>this.abort.abort(), 10000)
    try {
      if (this.points.length > 25) throw new Error("Too many points")
      const path = this.points.map(p=>p.coordinates.join(",")).join(";")
      const data = await this.json(`https://api.mapbox.com/directions/v5/mapbox/driving/${path}?access_token=${encodeURIComponent(this.tokenValue)}&geometries=geojson&overview=full&steps=false&alternatives=true&exclude=ferry`)
      if (!data.routes?.length) throw new Error("No route")
      data.routes.forEach((route,index)=>{
        const button=document.createElement("button")
        button.type="button"
        button.textContent=`Preview ${index+1}: about ${Math.round(route.distance/1609.344)} mi · ${(route.duration/3600).toFixed(1)} hr · not checked for clearance`
        button.addEventListener("click",()=>this.draw(route))
        this.optionsTarget.append(button)
      })
      this.draw(data.routes[0])
      const omitted=this.pointsValue.length-this.points.length
      this.statusTarget.textContent=`Not checked for clearance. Mapbox car-route estimates for this preview${omitted ? "; excludes " + omitted + " unmapped stops or endpoints" : ""}. Not coach navigation. Road data can be wrong or out of date. Obey posted signs. Alternative selection is not saved.`
    } catch (_) {
      this.statusTarget.textContent="Driving preview unavailable. No saved stops were changed. Reload the map to retry."
      this.optionsTarget.replaceChildren()
    } finally {
      clearTimeout(this.deadline)
      this.previewTarget.disabled=false
    }
  }

  // The saved TomTom truck route for each checked leg (server-provided
  // GeoJSON). Display only: the leg labels carry the verdict.
  truckRouteFeatures() {
    return (this.routesValue || []).filter(route => route?.geometry?.type === "LineString" && Array.isArray(route.geometry.coordinates) && route.geometry.coordinates.length >= 2 && route.geometry.coordinates.every(pair => this.coordinates(pair)))
      .map(route => ({type:"Feature",properties:{key:String(route.key)},geometry:route.geometry}))
  }

  drawTruckRoutes() {
    const features = this.truckRouteFeatures()
    if (!features.length || !this.map) return 0
    const data = {type:"FeatureCollection",features}
    if (this.map.getSource("trip-truck-routes")) this.map.getSource("trip-truck-routes").setData(data)
    else {
      this.map.addSource("trip-truck-routes",{type:"geojson",data})
      this.map.addLayer({id:"trip-truck-routes",type:"line",source:"trip-truck-routes",paint:{"line-color":"#d4af37","line-width":5}})
    }
    return features.length
  }

  draw(route) {
    const data={type:"Feature",geometry:route.geometry}
    if(this.map.getSource("trip-preview")) this.map.getSource("trip-preview").setData(data)
    else {
      this.map.addSource("trip-preview",{type:"geojson",data})
      this.map.addLayer({id:"trip-preview",type:"line",source:"trip-preview",paint:{"line-color":"#eabb67","line-width":4}})
    }
  }

  async changeStyle() { if(this.map) await this.load() }
  coordinates(value) { return Array.isArray(value) && value.length===2 && value.every(Number.isFinite) && Math.abs(value[0])<=180 && Math.abs(value[1])<=90 }
  async json(url) { const response=await fetch(url,{signal:this.abort.signal}); if(!response.ok) throw new Error("Provider unavailable"); return response.json() }
  wait(register) {
    return new Promise((resolve,reject)=>{
      if(this.abort.signal.aborted) return reject(new Error("Timed out"))
      this.abort.signal.addEventListener("abort",()=>reject(new Error("Timed out")),{once:true})
      register(resolve,reject)
    })
  }
  library() {
    if(window.mapboxgl) return Promise.resolve()
    return this.wait((resolve,reject)=>{
      const css=document.createElement("link"); css.rel="stylesheet"; css.href="https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css"; document.head.append(css)
      const script=document.createElement("script"); script.src="https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js"; script.onload=resolve; script.onerror=reject; document.head.append(script)
    })
  }
}
